import { isNil, keyBy, pick } from 'lodash/fp';
import { TypeFamily } from 'app/lib/types/server';
import { quoted, quoteIfNeeded } from 'app/lib/utils/sql';

const PREFIX = 'Source';

export const createDefaultFormula = (name) => `${PREFIX}.${quoteIfNeeded(name)}`;
const createDefaultFormulaWithForcedQuoting = (name) => `${PREFIX}.${quoted(name)}`;

export const hasUserFormula = (name, formula) =>
    formula !== createDefaultFormula(name) &&
    formula !== createDefaultFormulaWithForcedQuoting(name);
export const hasUserSuppliedFormula = ({ formula, pendingFormula, name }) => {
    const f = formula || pendingFormula;
    return Boolean(f) && hasUserFormula(name, f);
};

export const createColumn = ({
    name,
    typeFamily = TypeFamily.STRING,
    formula = null,
    pendingFormula = null,
    visible = true,
    hasError = false,
    hasMissingSourceColumn = false
}) => ({ name, typeFamily, formula, pendingFormula, visible, hasError, hasMissingSourceColumn });

export function rebaseColumnList({ base, local, remote }) {
    const baseByName = keyBy('name', base);
    const remoteByName = keyBy('name', remote);
    const columnList = [];

    for (const column of local) {
        const baseColumn = baseByName[column.name];
        const remoteColumn = remoteByName[column.name];

        if (isNil(baseColumn)) {
            columnList.push(column);
            continue;
        }

        const hasLocalFormula = column.formula !== baseColumn.formula;
        const hasLocalVisibility = column.visible !== baseColumn.visible;

        if (isNil(remoteColumn)) {
            if (hasLocalFormula || hasLocalVisibility || !isNil(column.pendingFormula)) {
                columnList.push(column);
            }
            continue;
        }

        columnList.push({
            ...remoteColumn,
            pendingFormula: column.pendingFormula,
            ...(hasLocalFormula && pick(['formula', 'hasError', 'hasMissingSourceColumn'], column)),
            ...(hasLocalVisibility && { visible: column.visible })
        });
    }

    let position = 0;

    for (const column of remote) {
        const index = columnList.findIndex(({ name }) => name === column.name);

        if (index !== -1) {
            position = index + 1;
        } else if (isNil(baseByName[column.name])) {
            columnList.splice(position++, 0, column);
        }
    }

    return columnList;
}
